import { useState } from 'react';
import { first } from 'lodash';

import { InlineButton } from '@/components/button';
import { Icon } from '@/components/icon';
import { Cell, Model } from '@/explore/types';
import { SearchInput } from '@/components/form/search-input';
import { createFieldOption } from '@/explore/components/select-options/field';
import { PipelineModel } from '@/explore/components/pipeline/pipeline-model';
import { getPipelineColorByCellId } from '@/explore/pipeline/format';
import { isRecordsLikeCell } from '@/core/cell';
import { buildExplorationFromCells, buildModelExploration } from '@/core/exploration';
import { generateShortId } from '@/lib/utils/string';

import { PipelineSearchModal } from '../../exploration-search/pipeline-search';
import { getFieldsByCellId } from '../utils';
import { isMasterCell } from '../../utils';
import { useMetadataContext } from '../../metadata-context';
import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';
import { useExplorationUIContext } from '../exploration-ui-context';
import { CombineStepState } from './types';
import { getDefaultJoinKey, pickFieldsForStep } from './utils';

import form from '@/components/form/form.module.scss';
import style from './combine.module.scss';

interface CombineItemEditorProps {
  item?: CombineStepState;
  previousItem?: CombineStepState;
  addMode?: boolean;
  setItem: (item: CombineStepState) => void;
  onDelete?: () => void;
}

export const CombineItemEditor = (props: CombineItemEditorProps) => {
  const [showList, setShowList] = useState(false);
  const { exploration } = useExplorationContext();
  const { models, metrics } = useMetadataContext();

  const handleSelectModel = (model: Model) => {
    const fields = model.properties.map(({ key, name, type }) => ({ key, name, type }));
    props.setItem({
      stepId: props.item?.stepId ?? generateShortId(),
      kind: 'model',
      modelId: model.modelId,
      title: model.name,
      fields,
      joinKey: getDefaultJoinKey(fields),
    });
    setShowList(false);
  };

  const handleSelectCell = (cell: Cell, dependencies: Cell[]) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }

    const explorationWithCells = buildExplorationFromCells([...dependencies, cell]);
    const fields = getFieldsByCellId(cell.id, explorationWithCells, models, [], metrics);

    props.setItem({
      stepId: props.item?.stepId ?? generateShortId(),
      kind: 'cell',
      cellId: cell.id,
      cells: [...dependencies, cell],
      title: cell.title ?? '',
      fields,
      joinKey: getDefaultJoinKey(fields),
    });
    setShowList(false);
  };

  return (
    <>
      {showList && (
        <PipelineSearchModal
          exploration={exploration}
          onSelectModel={handleSelectModel}
          onSelectCell={handleSelectCell}
          onClose={() => setShowList(false)}
        />
      )}

      {props.addMode === true ? (
        <InlineButton size="small" onClick={() => setShowList(true)}>
          <Icon name="Plus" size={15} /> Combine
        </InlineButton>
      ) : props.item === undefined ? (
        <div className={style.pipeline}>
          <button className={form.placeholderButton} onClick={() => setShowList(true)}>
            Select data…
          </button>
        </div>
      ) : (
        <CombineItemEditorInner
          item={props.item}
          previousItem={props.previousItem}
          setItem={props.setItem}
          onClick={() => setShowList(true)}
          onDelete={props.onDelete}
        />
      )}
    </>
  );
};

interface CombineItemEditorInnerProps {
  item: CombineStepState;
  previousItem?: CombineStepState;
  setItem: (item: CombineStepState) => void;
  onClick?: () => void;
  onDelete?: () => void;
}

const CombineItemEditorInner = (props: CombineItemEditorInnerProps) => {
  const { item } = props;
  const { exploration } = useExplorationContext();
  const { selectCell, scrollToCell, highlightCell } = useExplorationUIContext();
  const { getModel } = useMetadataContext();
  const { cell: parentCell } = useExplorationCellContext();

  const parentPipelineColor =
    item?.kind === 'cell' && isMasterCell(item.cellId, exploration)
      ? getPipelineColorByCellId(exploration, item.cellId)
      : undefined;

  const handleImport = () => {
    if (item === undefined || item.kind !== 'model') {
      throw new Error('Trying to import non-existent or invalid combine item');
    }

    const modelExploration = buildModelExploration(getModel(item.modelId));
    const cell = first(modelExploration.view.cells);
    if (cell === undefined || !('pipeline' in cell) || cell.pipeline.pipelineId === undefined) {
      throw new Error('Model exploration generation produced invalid cell');
    }

    props.setItem({ ...item, kind: 'cell', cellId: cell.id, cells: [cell] });

    selectCell(parentCell.id);
    scrollToCell(parentCell.id);
  };

  const handleScrollToBlock = () => {
    if (item?.kind !== 'cell') {
      return;
    }

    scrollToCell(item.cellId);
    highlightCell(item.cellId);
  };

  const fields = pickFieldsForStep(props.previousItem, item);

  return (
    <div className={style.pipeline}>
      <PipelineModel
        title="From"
        color={parentPipelineColor}
        onClick={props.onClick}
        accessories={[
          ...(item.kind === 'model'
            ? [{ icon: 'Instance' as const, title: 'Edit Pipeline', onClick: handleImport }]
            : []),
          ...(item.kind === 'cell'
            ? [
                {
                  icon: 'Crosshair' as const,
                  title: 'Find linked block',
                  onClick: handleScrollToBlock,
                },
              ]
            : []),
          ...(props.onDelete !== undefined
            ? [{ icon: 'Trash2' as const, title: 'Clear selection', onClick: props.onDelete }]
            : []),
        ]}>
        {item.title}
      </PipelineModel>

      {fields.length === 0 ? (
        <div className={form.placeholderButton}>
          No matching fields found. Please select another field from the opposite side.
        </div>
      ) : (
        <SearchInput
          options={fields.map(createFieldOption)}
          value={item.joinKey}
          onChange={(value) => props.setItem({ ...item, joinKey: value })}
        />
      )}
    </div>
  );
};
